import React, { useEffect, useRef, useState } from "react";
import FeatherIcon from "feather-icons-react";

/** Above this many people, Send stops to say out loud who is about to be contacted. */
export const CONFIRM_THRESHOLD = 25;

export const needsConfirm = (total) => Number(total || 0) > CONFIRM_THRESHOLD;

/**
 * The last thing between a manager and 264 emails.
 *
 * It does not ask "are you sure" — that question teaches people to click through. It states the
 * facts that would make somebody stop: how many people, how many units, which building, and on
 * which channels.
 */
const SendConfirm = ({ total, units, where, channels, companyWide, propertyCount, scheduled, onConfirm, onCancel, busy }) => (
  <div className="rn-confirm">
    <p className="rn-confirm-eyebrow">Before this goes out</p>
    <p className="rn-confirm-line">
      <strong>{total} residents</strong> across <strong>{units} units</strong>
    </p>
    <p className="rn-confirm-where">{where}</p>
    <p className="rn-confirm-ch">
      <FeatherIcon icon="send" size={13} /> {channels}
    </p>

    {companyWide ? (
      <p className="rn-confirm-warn">
        <FeatherIcon icon="alert-triangle" size={14} /> This reaches every resident at {propertyCount || "all"} properties, not
        just this building.
      </p>
    ) : null}

    {/* A scheduled notice has no undo window - it simply waits, and Cancel schedule is the
        way back. Promising 30 seconds there would be a promise the server does not keep. */}
    <p className="rn-confirm-note">
      {scheduled
        ? "Nothing goes out until the time you picked. You can cancel the schedule until then."
        : "You will have 30 seconds to stop it afterwards."}
    </p>

    <div className="rn-confirm-actions">
      <button type="button" className="rn-btn" onClick={onCancel} disabled={busy}>
        Back
      </button>
      <button type="button" className="rn-btn rn-btn-primary" onClick={onConfirm} disabled={busy}>
        Send to <span className="rn-confirm-n">{total}</span> resident{total === 1 ? "" : "s"}
      </button>
    </div>
  </div>
);

/**
 * The thirty seconds after Send. Nothing has left yet; the server is holding the notice and
 * the timer here only mirrors that. `onDone` fires when the window shuts so the modal can move
 * on without waiting for the send itself.
 *
 * The callback is held in a ref so a parent that re-renders (or hands over a fresh arrow every
 * render) cannot restart the tick, and the timeout is cleared on unmount so a closed modal
 * never keeps counting.
 */
export const UndoBar = ({ seconds = 30, onUndo, onDone }) => {
  const [left, setLeft] = useState(seconds);
  const leftRef = useRef(seconds);
  const doneRef = useRef(onDone);
  const firedRef = useRef(false);
  doneRef.current = onDone;

  // One interval for the whole window rather than a fresh timeout per tick: the countdown does
  // not depend on a re-render landing in time, and the interval is cleared on unmount so a
  // closed modal can never keep ticking.
  useEffect(() => {
    firedRef.current = false;
    leftRef.current = seconds;
    setLeft(seconds);
    const finish = () => {
      if (firedRef.current) return;
      firedRef.current = true;
      if (doneRef.current) doneRef.current();
    };
    if (seconds <= 0) {
      finish();
      return undefined;
    }
    const t = setInterval(() => {
      leftRef.current -= 1;
      setLeft(leftRef.current > 0 ? leftRef.current : 0);
      if (leftRef.current <= 0) {
        clearInterval(t);
        finish();
      }
    }, 1000);
    return () => clearInterval(t);
  }, [seconds]);

  return (
    <div className="rn-undo">
      <span className="rn-undo-text">
        <FeatherIcon icon="clock" size={14} /> Going out in <strong>{left}s</strong>. Nothing has been sent yet.
      </span>
      <button type="button" className="rn-btn rn-btn-danger" onClick={onUndo}>
        Undo
      </button>
    </div>
  );
};

export default SendConfirm;
